// 문의 관리 › 관리자 탭 = 직원 계정 관리(시안 viewAdmins·mAdmin, 계약 §6). 대표만 본다(InquiryPage 가 탭을 숨긴다).
// 목록·가입 코드는 여기서 직접 읽고, 임시 비밀번호는 한 번만 보이는 창에 띄운다.
import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./v6Api";
import { kstFull } from "./view";
import { useDismiss } from "../v6common/a11y";

const EMPTY = { name: "", email: "", team: "" };
const SEARCH_DEBOUNCE_MS = 300;
const ARM_MS = 3000; // 삭제 「한 번 더 누르면 삭제」가 풀리는 시간
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const STATUS_TAG = {
    active: ["tag-ok", "사용 중"],
    disabled: ["tag-exp", "사용 중지"],
    pending_first_login: ["tag-esc", "첫 로그인 전"],
};
const openCountOf = (e) => e?.response?.data?.detail?.open_count;
const COPY_FAIL = "복사하지 못했습니다. 화면의 값을 직접 옮겨 적어 주세요.";
// 클립보드 쓰기는 비동기다 — 실패(권한 없음·http)도 알린다
async function copyText(text, ok, notify) {
    try {
        if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
        await navigator.clipboard.writeText(text);
        notify(ok, "success");
    } catch { notify(COPY_FAIL, "error"); }
}

// 임시 비밀번호 창 — 닫으면 다시 볼 수 없다(서버도 다시 주지 않는다). 그래서 바깥 클릭·Esc 로는 닫지 않고 버튼으로만 닫는다.
function TempPasswordModal({ info, onClose, notify }) {
    const copy = () => copyText(info.password, "임시 비밀번호를 복사했습니다.", notify);
    return (
        <div className="modal on">
            <div className="mbox" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
                <div className="mh"><h3>{info.title}</h3><button className="btn btn-ghost btn-sm x" onClick={onClose} aria-label="닫기"><i className="fa-solid fa-xmark"></i></button></div>
                <div className="mb">
                    <div className="codebox" style={{ margin: "0 0 12px" }}>
                        <div><div className="k">{info.name} · 임시 비밀번호</div><div className="code" style={{ letterSpacing: 2 }}>{info.password}</div></div>
                        <span className="sp"></span>
                        <button className="btn btn-sm" onClick={copy}><i className="fa-regular fa-copy"></i>복사</button>
                    </div>
                    <div className="note" style={{ margin: 0 }}>
                        이 비밀번호는 지금 <b>한 번만 보입니다</b>. 직원에게 전달하세요{info.expiresAt ? ` · ${kstFull(info.expiresAt)}까지` : ""}.
                        직원이 이 비밀번호로 로그인하면 바로 새 비밀번호를 정하게 됩니다.
                    </div>
                </div>
                <div className="mf"><button className="btn btn-pri" onClick={onClose}>확인했습니다</button></div>
            </div>
        </div>
    );
}

export default function AdminsTab({ me, meta, notify }) {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [query, setQuery] = useState("");
    const [code, setCode] = useState(null);
    const [form, setForm] = useState(null); // null이면 추가 창 닫힘
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [temp, setTemp] = useState(null); // 임시 비밀번호 창
    const [armed, setArmed] = useState(null); // 삭제 확정 대기 중인 직원 id
    const armTimer = useRef(null);
    const dismiss = useDismiss(() => setForm(null), { enabled: !!form });
    const teams = meta?.teams || [];

    useEffect(() => {
        const t = setTimeout(() => setQuery(q), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [q]);

    const reload = useCallback(() => api.listStaff(query.trim() || undefined).then(setRows).catch((e) => notify(api.errorText(e), "error")), [query, notify]);
    useEffect(() => { reload(); }, [reload]);
    const loadCode = useCallback(() => api.getJoinCode().then(setCode).catch(() => setCode(null)), []);
    useEffect(() => { loadCode(); }, [loadCode]);
    useEffect(() => () => clearTimeout(armTimer.current), []);

    // 직원을 중지·삭제하면 서버가 가입 코드를 바꾼다 — 알리고 코드 칸을 다시 읽는다
    const afterRotate = (r) => {
        if (r?.join_code_rotated) { notify("가입 코드가 바뀌었습니다. 새 코드를 직원에게 전달하세요.", "info"); loadCode(); }
    };
    const openCountMsg = (s, e, verb) => `${s.name}님이 담당 중인 문의가 ${openCountOf(e) ?? s.open_count}건 있어요. 직원별 업무에서 다른 사람에게 넘긴 뒤 ${verb}해 주세요.`;

    const save = async () => {
        const name = form.name.trim(), email = form.email.trim();
        const e = [];
        if (!name) e.push("이름");
        if (!EMAIL_RE.test(email)) e.push("이메일 형식");
        if (e.length) { setErr(`${e.join(", ")}을(를) 확인해 주세요.`); return; }
        setBusy(true);
        try {
            const r = await api.createStaff({ name, email, team: form.team || teams[0] || "" });
            setForm(null);
            setTemp({ title: "직원을 등록했습니다", name: r.name, password: r.temporary_password, expiresAt: r.temporary_password_expires_at });
            reload();
        } catch (ex) {
            setErr(api.errorText(ex));
        } finally { setBusy(false); }
    };

    const toggle = async (s) => {
        try {
            if (s.status === "disabled") {
                await api.enableStaff(s.id);
                notify(`${s.name} 계정을 다시 사용합니다.`, "success");
            } else {
                const r = await api.disableStaff(s.id);
                notify(`${s.name} 계정을 사용 중지했습니다. 로그인이 막히고 담당자 목록에서 빠집니다.`, "warning");
                afterRotate(r);
            }
            reload();
        } catch (e) {
            if (e?.response?.data?.detail?.code === "has_open_inquiries") notify(openCountMsg(s, e, "중지"), "warning");
            else notify(api.errorText(e), "error");
        }
    };

    const remove = async (s) => {
        if (armed !== s.id) {
            setArmed(s.id);
            clearTimeout(armTimer.current);
            armTimer.current = setTimeout(() => setArmed(null), ARM_MS);
            return;
        }
        clearTimeout(armTimer.current); setArmed(null);
        try {
            const r = await api.deleteStaff(s.id);
            notify(`${s.name} 계정을 삭제했습니다. 처리 이력에는 이름이 남습니다.`, "warning");
            afterRotate(r);
            reload();
        } catch (e) {
            if (e?.response?.data?.detail?.code === "has_open_inquiries") notify(openCountMsg(s, e, "삭제"), "warning");
            else notify(api.errorText(e), "error");
        }
    };

    const reissue = async (s) => {
        try {
            const r = await api.resetStaffPassword(s.id);
            setTemp({ title: "비밀번호를 재발급했습니다", name: s.name, password: r.temporary_password, expiresAt: r.expires_at });
            reload();
        } catch (e) { notify(api.errorText(e), "error"); }
    };

    const copyCode = () => copyText(code?.code || "", "가입 코드를 복사했습니다. 직원에게 전달하세요.", notify);
    const rotate = async () => {
        try { setCode(await api.rotateJoinCode()); notify("새 가입 코드를 만들었습니다. 이전 코드는 더 이상 쓸 수 없습니다.", "success"); }
        catch (e) { notify(api.errorText(e), "error"); }
    };

    const active = rows.filter((r) => r.status !== "disabled").length;
    const off = rows.length - active;
    return (
        <div>
            <div className="pn-h">
                <h3>직원 계정</h3><span className="hint">{active}명 사용 중{off ? ` · ${off}명 사용 중지` : ""}</span><span className="sp"></span>
                <input className="inp" placeholder="이름 · 이메일 · 소속" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} />
                <button className="btn btn-sm" onClick={() => { setForm({ ...EMPTY, team: teams[0] || "" }); setErr(""); }}><i className="fa-solid fa-user-plus"></i>직접 추가</button>
            </div>
            <div className="codebox">
                <div><div className="k">회사 가입 코드</div><div className="code">{code?.code || "------"}</div></div>
                <div style={{ fontSize: 12.5, color: "var(--tx2)", lineHeight: 1.6 }}>
                    직원이 로그인 화면의 「계정 만들기」에서 이 코드를 넣으면 바로 직원 계정으로 문의 관리를 쓸 수 있습니다.<br />
                    코드가 밖으로 새면 「새 코드 만들기」로 바꾸세요. 이미 가입한 계정은 영향이 없습니다.
                </div>
                <span className="sp"></span>
                <button className="btn btn-sm" onClick={copyCode} disabled={!code}><i className="fa-regular fa-copy"></i>복사</button>
                <button className="btn btn-sm" onClick={rotate}><i className="fa-solid fa-rotate"></i>새 코드 만들기</button>
            </div>
            <div className="tbl-wrap" style={{ maxHeight: "none", minHeight: 0 }}>
                <table className="tbl">
                    <thead><tr><th>이름</th><th>이메일</th><th>소속</th><th>권한</th><th className="num">담당 중</th><th className="num">완료</th><th>가입일</th><th>마지막 로그인</th><th>상태</th><th style={{ width: 230 }}></th></tr></thead>
                    <tbody>
                        {rows.length === 0 ? <tr><td colSpan={10}><div className="empty">조건에 맞는 직원이 없습니다</div></td></tr> : rows.map((s) => {
                            const [cls, label] = STATUS_TAG[s.status] || ["tag-gen", s.status];
                            const isOwnerRow = s.role === "owner";
                            const locked = isOwnerRow || s.is_me || s.id === me?.id; // 대표 계정·자기 자신은 서버도 403
                            return (
                                <tr key={s.id} className={s.status === "disabled" ? "off" : ""}>
                                    <td><b>{s.name}</b>{(s.is_me || s.id === me?.id) && <> <span className="tag tag-ok">현재 사용자</span></>}</td>
                                    <td className="c-dt">{s.email}</td>
                                    <td>{s.team || "소속 없음"}</td>
                                    <td>{isOwnerRow ? <span className="tag tag-res">대표관리자</span> : <span className="tag tag-gen">직원</span>}</td>
                                    <td className="num">{s.open_count ?? 0}</td>
                                    <td className="num">{s.completed_count ?? 0}</td>
                                    <td className="c-dt">{s.joined_at ? kstFull(s.joined_at).slice(0, 10) : ""}</td>
                                    <td className="c-dt">{s.last_login_at ? kstFull(s.last_login_at) : "아직 없음"}</td>
                                    <td><span className={`tag ${cls}`}>{label}</span></td>
                                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                        {locked ? <span className="note" style={{ margin: 0 }}>{isOwnerRow ? "가람 계정 · 고정" : "내 계정"}</span> : (
                                            <>
                                                <button className="btn btn-sm" title="비밀번호 재발급" onClick={() => reissue(s)}><i className="fa-solid fa-key"></i></button>{" "}
                                                <button className="btn btn-sm" title={s.status === "disabled" ? "다시 사용" : "사용 중지"} onClick={() => toggle(s)}>
                                                    {s.status === "disabled" ? <><i className="fa-solid fa-play"></i>다시 사용</> : <><i className="fa-solid fa-pause"></i>사용 중지</>}
                                                </button>{" "}
                                                <button className="btn btn-sm btn-danger" title="삭제" onClick={() => remove(s)}>
                                                    {armed === s.id ? "한 번 더 누르면 삭제" : <i className="fa-regular fa-trash-can"></i>}
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="board-hint"><i className="fa-solid fa-circle-info"></i> 권한은 대표관리자(전체 메뉴·직원 계정 관리) 하나와 직원(문의 관리) 둘뿐입니다. 퇴사자는 삭제 대신 「사용 중지」로 두면 로그인만 막히고 처리 이력의 이름은 남습니다. 담당 중인 문의가 있으면 직원별 업무에서 넘긴 뒤 중지·삭제합니다. 메일을 못 받는 직원은 「비밀번호 재발급」으로 임시 비밀번호를 직접 전달하세요.</div>

            {form && (
                <div className="modal on" {...dismiss}>
                    <div className="mbox" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
                        <div className="mh"><h3>직원 직접 추가</h3><button className="btn btn-ghost btn-sm x" onClick={() => setForm(null)} aria-label="닫기"><i className="fa-solid fa-xmark"></i></button></div>
                        <div className="mb">
                            <div className="grid2" style={{ marginBottom: 12 }}>
                                <div className="fld"><label htmlFor="adName">이름 <span className="req">*</span></label><input id="adName" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                                <div className="fld"><label htmlFor="adTeam">소속</label>
                                    <select id="adTeam" className="sel" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })}>
                                        {teams.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select></div>
                            </div>
                            <div className="fld"><label htmlFor="adMail">이메일 <span className="req">*</span></label><input id="adMail" className="inp" placeholder="로그인 아이디로 씁니다" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                            <div className="help" style={{ marginTop: 8 }}>등록하면 임시 비밀번호(72시간)가 한 번 보이고, 메일이 켜져 있으면 이 이메일로도 갑니다. 직원은 첫 로그인에서 새 비밀번호를 정합니다.</div>
                            <div className={`err ${err ? "on" : ""}`}>{err}</div>
                        </div>
                        <div className="mf"><button className="btn" onClick={() => setForm(null)}>취소</button><button className="btn btn-pri" disabled={busy} onClick={save}><i className="fa-solid fa-check"></i>등록</button></div>
                    </div>
                </div>
            )}
            {temp && <TempPasswordModal info={temp} onClose={() => setTemp(null)} notify={notify} />}
        </div>
    );
}
